import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Clock, MapPin, CheckCircle2, AlertTriangle } from "lucide-react";

export default function AdminAttendance() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/time-entries/admin${d ? `?date_filter=${d}` : ""}`);
      setEntries(res.data || []);
    } catch {
      toast.error("Error al cargar asistencia");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(dateFilter); }, [dateFilter]);

  const calcHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return null;
    const diff = (new Date(checkOut) - new Date(checkIn)) / 1000 / 60 / 60;
    return diff.toFixed(1);
  };

  return (
    <div data-testid="admin-attendance-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFD62B]/20 flex items-center justify-center">
            <Clock className="w-5 h-5 text-[#FFD62B]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Asistencia de Técnicos</h1>
            <p className="text-sm text-muted-foreground">Registro de entrada y salida con ubicación GPS</p>
          </div>
        </div>
        <Input
          type="date"
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          className="w-44 shrink-0"
          data-testid="attendance-date-filter"
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : entries.length === 0 ? (
        <Card className="p-8 text-center">
          <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Sin registros de asistencia para esta fecha</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map(e => {
            const hrs = calcHours(e.check_in, e.check_out);
            const complete = e.check_in && e.check_out;
            return (
              <Card key={e.id} className="p-4" data-testid={`attendance-entry-${e.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    complete ? "bg-green-100" : e.check_in ? "bg-amber-100" : "bg-zinc-100"
                  }`}>
                    {complete
                      ? <CheckCircle2 className="w-4 h-4 text-green-700" />
                      : e.check_in
                        ? <Clock className="w-4 h-4 text-amber-700" />
                        : <AlertTriangle className="w-4 h-4 text-zinc-500" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-semibold">{e.technician_name}</span>
                      {complete
                        ? <Badge className="bg-green-100 text-green-800 border-green-300 text-[10px]">Completo</Badge>
                        : e.check_in
                          ? <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">En jornada</Badge>
                          : <Badge className="bg-zinc-100 text-zinc-600 border-zinc-300 text-[10px]">Sin marcar</Badge>
                      }
                      {hrs && <span className="text-xs font-mono text-muted-foreground">{hrs}h</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                      {e.check_in && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Entrada:</span>
                          <span className="font-mono font-medium">
                            {new Date(e.check_in).toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {e.check_in_latitude && (
                            <a href={`https://www.google.com/maps?q=${e.check_in_latitude},${e.check_in_longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              <MapPin className="w-3 h-3 inline" />
                            </a>
                          )}
                        </div>
                      )}
                      {e.check_out && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Salida:</span>
                          <span className="font-mono font-medium">
                            {new Date(e.check_out).toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {e.check_out_latitude && (
                            <a href={`https://www.google.com/maps?q=${e.check_out_latitude},${e.check_out_longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              <MapPin className="w-3 h-3 inline" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
